/**
 * Comprehensive Live Test Suite
 * 
 * Tests all API calls, JSON schemas, database queries, agent calls,
 * and agentic workflows using stored authentication.
 * 
 * This validates end-to-end functionality with real Microsoft Graph API calls.
 */

import React, { useState, useEffect } from 'react';
import { createClient } from '@blinkdotnew/sdk';
import { emailActions } from '../services/emailActions';
import { gpt5NanoAgent, executeUserIntent } from '../services/gpt5NanoAgent';
import { unifiedAIAgentService } from '../services/unifiedAIAgentService';
import { structuredEmailAI } from '../services/structuredEmailAI';
import { gpt5Orchestrator } from '../services/gpt5Orchestrator';
import { testResultReporter, TestResultDetails } from '../services/testResultReporter';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Separator } from '../components/ui/separator';
import { Alert, AlertDescription } from '../components/ui/alert';
import { CheckCircle2, XCircle, Clock, AlertCircle, Play, RefreshCw, Mail, Download, Database } from 'lucide-react';
import { toast } from 'sonner';

const blink = createClient({
  projectId: 'lemon-ui-agentic-email-ai-agent-ben3ou87',
  authRequired: false
});

// Use TestResultDetails from testResultReporter
type TestResult = TestResultDetails;

export default function ComprehensiveLiveTest() {
  const [user, setUser] = useState<any>(null);
  const [emailAccount, setEmailAccount] = useState<any>(null);
  const [testResults, setTestResults] = useState<TestResult[]>([]);
  const [isRunning, setIsRunning] = useState(false);
  const [isSendingReport, setIsSendingReport] = useState(false);
  const [currentTest, setCurrentTest] = useState<string>('');
  const [lastReportSent, setLastReportSent] = useState<string | null>(null);
  const [summary, setSummary] = useState({
    total: 0,
    passed: 0,
    failed: 0,
    skipped: 0,
    duration: 0
  });

  // Authentication check
  useEffect(() => {
    const unsubscribe = blink.auth.onAuthStateChanged(async (state) => {
      if (state.user) {
        setUser(state.user);
        
        // Get email account for live testing
        const accounts = await blink.db.emailAccounts.list({
          where: { userId: state.user.id },
          limit: 1
        });
        
        if (accounts.length > 0) {
          setEmailAccount(accounts[0]);
          emailActions.setUserId(state.user.id);
          await unifiedAIAgentService.initialize(state.user.id);
          
          // Initialize test result reporter
          await testResultReporter.initialize(state.user.id, state.user.email);
        }
      }
    });

    return unsubscribe;
  }, []);

  // Test definitions with enhanced metadata
  const tests: TestResult[] = [
    // Authentication & Database Tests
    {
      id: 'auth-check',
      name: 'Verify User Authentication',
      category: 'Authentication',
      status: 'pending',
      authMethod: 'blink_auth',
      userInput: 'System check for authenticated user session'
    },
    {
      id: 'db-tokens',
      name: 'Retrieve OAuth Tokens from Database',
      category: 'Database',
      status: 'pending',
      authMethod: 'oauth_outlook',
      userInput: 'SELECT * FROM email_accounts WHERE user_id = ?'
    },
    {
      id: 'db-accounts',
      name: 'Query Email Accounts Table',
      category: 'Database',
      status: 'pending',
      authMethod: 'blink_auth',
      userInput: 'blink.db.emailAccounts.list()'
    },
    
    // Email Action Tests
    {
      id: 'email-search',
      name: 'Search Emails (Live Graph API)',
      category: 'Email Actions',
      status: 'pending',
      authMethod: 'oauth_outlook',
      graphApiEndpoint: '/me/messages',
      httpMethod: 'GET',
      userInput: 'Search for emails with query: "test"'
    },
    {
      id: 'email-send-test',
      name: 'Send Test Email (Live Graph API)',
      category: 'Email Actions',
      status: 'pending',
      authMethod: 'oauth_outlook',
      graphApiEndpoint: '/me/sendMail',
      httpMethod: 'POST',
      userInput: 'Send test email to self with timestamp'
    },
    {
      id: 'email-draft',
      name: 'Create Email Draft',
      category: 'Email Actions',
      status: 'pending',
      authMethod: 'oauth_outlook',
      graphApiEndpoint: '/me/messages',
      httpMethod: 'POST',
      userInput: 'Create draft email to self'
    },
    {
      id: 'email-archive',
      name: 'Archive Email Operation',
      category: 'Email Actions',
      status: 'pending',
      authMethod: 'blink_auth',
      userInput: 'Archive first email in inbox'
    },
    
    // Agent Tests
    {
      id: 'agent-gpt5-nano',
      name: 'GPT-5 Nano Agent Processing',
      category: 'AI Agents',
      status: 'pending',
      authMethod: 'blink_auth',
      apiCallType: 'ai_inference',
      userInput: 'Search for emails from the last week about meetings'
    },
    {
      id: 'agent-structured',
      name: 'Structured Email AI Agent',
      category: 'AI Agents',
      status: 'pending',
      authMethod: 'blink_auth',
      apiCallType: 'ai_inference',
      userInput: 'Create a draft email to test@example.com about the project update'
    },
    {
      id: 'agent-orchestrator',
      name: 'GPT-5 Orchestrator Workflow',
      category: 'AI Agents',
      status: 'pending',
      authMethod: 'blink_auth',
      apiCallType: 'ai_planning',
      userInput: 'Find all unread emails from this week'
    },
    {
      id: 'agent-unified',
      name: 'Unified AI Agent Service',
      category: 'AI Agents',
      status: 'pending',
      authMethod: 'blink_auth',
      apiCallType: 'service_status',
      userInput: 'Check if unified AI agent is initialized'
    },
    
    // Agentic Workflow Tests
    {
      id: 'workflow-email-send',
      name: 'Agentic Email Send Workflow',
      category: 'Workflows',
      status: 'pending',
      authMethod: 'oauth_outlook',
      graphApiEndpoint: '/me/sendMail',
      httpMethod: 'POST',
      userInput: 'Send a test email to [user_email] with subject "Workflow Test"'
    },
    {
      id: 'workflow-email-search',
      name: 'Agentic Email Search Workflow',
      category: 'Workflows',
      status: 'pending',
      authMethod: 'oauth_outlook',
      graphApiEndpoint: '/me/messages',
      httpMethod: 'GET',
      userInput: 'Search for emails about testing'
    },
    {
      id: 'workflow-calendar-create',
      name: 'Agentic Calendar Create Workflow',
      category: 'Workflows',
      status: 'pending',
      authMethod: 'oauth_outlook',
      graphApiEndpoint: '/me/calendar/events',
      httpMethod: 'POST',
      userInput: 'Create a calendar event titled "Test Meeting" tomorrow at 10 AM'
    },
    
    // JSON Schema Tests
    {
      id: 'json-email-response',
      name: 'Validate Email Response JSON Schema',
      category: 'JSON Schemas',
      status: 'pending',
      authMethod: 'blink_auth',
      expectedResult: 'Email object with required fields: id, userId, subject, senderEmail, bodyText'
    },
    {
      id: 'json-agent-response',
      name: 'Validate Agent Response JSON Schema',
      category: 'JSON Schemas',
      status: 'pending',
      authMethod: 'blink_auth',
      expectedResult: 'Agent response with: thinking, action, reasoning, validation'
    },
    
    // Calendar Tests
    {
      id: 'calendar-search',
      name: 'Search Calendar Events',
      category: 'Calendar',
      status: 'pending',
      authMethod: 'oauth_outlook',
      graphApiEndpoint: '/me/calendar/calendarView',
      httpMethod: 'GET',
      userInput: 'Search calendar events for next 7 days with query: "meeting"'
    }
  ];

  // Update test result
  const updateTest = (id: string, updates: Partial<TestResult>) => {
    setTestResults(prev => {
      const updated = prev.map(test => 
        test.id === id ? { ...test, ...updates } : test
      );
      return updated;
    });
  };

  // Execute individual test
  const executeTest = async (test: TestResult): Promise<void> => {
    setCurrentTest(test.name);
    const startTime = Date.now();
    
    updateTest(test.id, {
      status: 'running',
      startTime
    });

    try {
      let result: any;
      const apiCalls: any[] = [];

      switch (test.id) {
        // Authentication Tests
        case 'auth-check':
          if (!user) throw new Error('User not authenticated');
          result = {
            userId: user.id,
            email: user.email,
            authenticated: true
          };
          break;

        // Database Tests
        case 'db-tokens':
          if (!emailAccount) throw new Error('No email account found');
          result = {
            accountId: emailAccount.id,
            provider: emailAccount.provider,
            email: emailAccount.emailAddress || emailAccount.email_address,
            hasAccessToken: !!(emailAccount.accessToken || emailAccount.access_token),
            hasRefreshToken: !!(emailAccount.refreshToken || emailAccount.refresh_token),
            tokenExpiry: emailAccount.tokenExpiresAt || emailAccount.token_expires_at
          };
          break;

        case 'db-accounts':
          const accounts = await blink.db.emailAccounts.list({
            where: { userId: user.id }
          });
          result = {
            accountCount: accounts.length,
            accounts: accounts.map((acc: any) => ({
              id: acc.id,
              provider: acc.provider,
              email: acc.emailAddress || acc.email_address
            }))
          };
          break;

        // Email Action Tests
        case 'email-search':
          console.log('🔍 Testing live email search via Graph API...');
          const searchResults = await emailActions.search({
            query: 'test',
            isStarred: false
          });
          result = {
            resultsCount: searchResults.length,
            sampleResults: searchResults.slice(0, 3).map((email: any) => ({
              id: email.id,
              subject: email.subject,
              from: email.senderEmail,
              date: email.receivedDate
            }))
          };
          apiCalls.push({
            endpoint: 'Microsoft Graph API - Messages Search',
            method: 'GET',
            status: 200,
            duration: Date.now() - startTime
          });
          break;

        case 'email-send-test':
          console.log('📧 Testing live email send via Graph API...');
          const sendResult = await emailActions.sendEmail({
            to: [user.email], // Send to self for testing
            subject: `Test Email - ${new Date().toLocaleString()}`,
            body: 'This is a test email sent via the Comprehensive Live Test Suite.',
            htmlBody: '<h3>Test Email</h3><p>This is a test email sent via the Comprehensive Live Test Suite.</p>',
            importance: 'normal'
          });
          result = {
            success: sendResult.success,
            messageId: sendResult.messageId,
            recipient: user.email
          };
          apiCalls.push({
            endpoint: 'Microsoft Graph API - Send Mail',
            method: 'POST',
            status: 201,
            duration: Date.now() - startTime
          });
          break;

        case 'email-draft':
          const draft = await emailActions.createDraft({
            to: [user.email],
            subject: 'Draft Test',
            body: 'This is a test draft.'
          });
          result = {
            draftId: draft.id,
            subject: draft.subject
          };
          break;

        case 'email-archive':
          // Get first email to test archiving
          const emailsToArchive = await blink.db.messages_enhanced.list({
            where: { userId: user.id },
            limit: 1
          });
          if (emailsToArchive.length > 0) {
            await emailActions.archive(emailsToArchive[0].id);
            result = {
              archived: true,
              emailId: emailsToArchive[0].id
            };
          } else {
            throw new Error('No emails available to test archiving');
          }
          break;

        // Agent Tests
        case 'agent-gpt5-nano':
          console.log('🤖 Testing GPT-5 Nano Agent...');
          const nanoResponse = await gpt5NanoAgent.processUserIntent(
            'Search for emails from the last week about meetings'
          );
          result = {
            actionType: nanoResponse.action.type,
            confidence: nanoResponse.action.confidence,
            isValid: nanoResponse.validation.isValid,
            hasThinking: !!nanoResponse.thinking,
            hasReasoning: !!nanoResponse.reasoning
          };
          break;

        case 'agent-structured':
          console.log('🤖 Testing Structured Email AI...');
          const structuredResult = await structuredEmailAI.executeUserIntent(
            'Create a draft email to test@example.com about the project update'
          );
          result = {
            success: structuredResult.success,
            actionType: structuredResult.action
          };
          break;

        case 'agent-orchestrator':
          console.log('🤖 Testing GPT-5 Orchestrator...');
          const plan = await gpt5Orchestrator.planExecution(
            'Find all unread emails from this week'
          );
          result = {
            hasPlan: !!plan,
            steps: plan?.steps?.length || 0
          };
          break;

        case 'agent-unified':
          console.log('🤖 Testing Unified AI Agent Service...');
          const status = unifiedAIAgentService.getStatus();
          result = {
            isInitialized: status.isInitialized,
            hasGraphApiAccess: status.hasGraphApiAccess
          };
          break;

        // Workflow Tests
        case 'workflow-email-send':
          console.log('⚙️ Testing agentic email send workflow...');
          const sendWorkflow = await executeUserIntent(
            `Send a test email to ${user.email} with subject "Workflow Test"`
          );
          result = {
            success: sendWorkflow.success,
            action: sendWorkflow.agent_response?.action?.type,
            executed: !!sendWorkflow.execution_result
          };
          break;

        case 'workflow-email-search':
          console.log('⚙️ Testing agentic email search workflow...');
          const searchWorkflow = await executeUserIntent(
            'Search for emails about testing'
          );
          result = {
            success: searchWorkflow.success,
            action: searchWorkflow.agent_response?.action?.type,
            resultsFound: searchWorkflow.execution_result?.resultsCount || 0
          };
          break;

        case 'workflow-calendar-create':
          console.log('⚙️ Testing agentic calendar create workflow...');
          const tomorrow = new Date();
          tomorrow.setDate(tomorrow.getDate() + 1);
          tomorrow.setHours(10, 0, 0, 0);
          
          const endTime = new Date(tomorrow);
          endTime.setHours(11, 0, 0, 0);
          
          const calendarWorkflow = await executeUserIntent(
            `Create a calendar event titled "Test Meeting" tomorrow at 10 AM`
          );
          result = {
            success: calendarWorkflow.success,
            action: calendarWorkflow.agent_response?.action?.type
          };
          break;

        // JSON Schema Tests
        case 'json-email-response':
          const sampleEmail = await blink.db.messages_enhanced.list({
            where: { userId: user.id },
            limit: 1
          });
          if (sampleEmail.length > 0) {
            const email = sampleEmail[0];
            const requiredFields = ['id', 'userId', 'subject', 'senderEmail', 'bodyText'];
            const hasAllFields = requiredFields.every(field => email.hasOwnProperty(field));
            result = {
              valid: hasAllFields,
              sampleFields: Object.keys(email).slice(0, 10),
              missingFields: requiredFields.filter(field => !email.hasOwnProperty(field))
            };
          } else {
            throw new Error('No emails to validate schema');
          }
          break;

        case 'json-agent-response':
          const agentTestResponse = await gpt5NanoAgent.processUserIntent('Test message');
          const hasRequiredFields = 
            agentTestResponse.hasOwnProperty('thinking') &&
            agentTestResponse.hasOwnProperty('action') &&
            agentTestResponse.hasOwnProperty('reasoning') &&
            agentTestResponse.hasOwnProperty('validation');
          result = {
            valid: hasRequiredFields,
            structure: {
              hasThinking: !!agentTestResponse.thinking,
              hasAction: !!agentTestResponse.action,
              hasReasoning: !!agentTestResponse.reasoning,
              hasValidation: !!agentTestResponse.validation
            }
          };
          break;

        // Calendar Tests
        case 'calendar-search':
          const { unifiedCalendarService } = await import('../services/calendar/unifiedCalendarService');
          const startDate = new Date();
          const endDate = new Date();
          endDate.setDate(endDate.getDate() + 7);
          
          const events = await unifiedCalendarService.getEventsByDateRange(
            startDate,
            endDate,
            'meeting'
          );
          result = {
            eventsFound: events.length,
            dateRange: {
              start: startDate.toISOString(),
              end: endDate.toISOString()
            }
          };
          break;

        default:
          throw new Error('Unknown test');
      }

      const endTime = Date.now();
      const duration = endTime - startTime;

      updateTest(test.id, {
        status: 'success',
        endTime,
        duration,
        result,
        apiCalls: apiCalls.length > 0 ? apiCalls : undefined,
        details: `Completed in ${duration}ms`
      });

      console.log(`✅ Test ${test.name} passed in ${duration}ms`);

    } catch (error) {
      const endTime = Date.now();
      const duration = endTime - startTime;
      
      updateTest(test.id, {
        status: 'failed',
        endTime,
        duration,
        error: error instanceof Error ? error.message : 'Unknown error',
        details: `Failed after ${duration}ms`
      });

      console.error(`❌ Test ${test.name} failed:`, error);
    }
  };

  // Run all tests
  const runAllTests = async () => {
    if (!user) {
      alert('Please sign in first');
      return;
    }

    if (!emailAccount) {
      alert('Please connect an email account first');
      return;
    }

    setIsRunning(true);
    setTestResults(tests);
    
    const startTime = Date.now();

    for (const test of tests) {
      await executeTest(test);
      // Small delay between tests
      await new Promise(resolve => setTimeout(resolve, 500));
    }

    const endTime = Date.now();
    const totalDuration = endTime - startTime;

    // Calculate summary
    const results = testResults;
    const passed = results.filter(t => t.status === 'success').length;
    const failed = results.filter(t => t.status === 'failed').length;
    const skipped = results.filter(t => t.status === 'skipped').length;

    setSummary({
      total: results.length,
      passed,
      failed,
      skipped,
      duration: totalDuration
    });

    setIsRunning(false);
    setCurrentTest('');

    console.log('📊 Test Suite Complete:', {
      total: results.length,
      passed,
      failed,
      skipped,
      duration: `${totalDuration}ms`
    });

    // Automatically send report via Outlook if tests are complete
    if (testResults.length > 0) {
      toast.info('Test suite complete! Click "Send Report via Outlook" to email results.');
    }
  };

  // Reset tests
  const resetTests = () => {
    setTestResults([]);
    setSummary({
      total: 0,
      passed: 0,
      failed: 0,
      skipped: 0,
      duration: 0
    });
    setCurrentTest('');
    setLastReportSent(null);
  };

  // Send report via Outlook
  const sendReportViaOutlook = async () => {
    if (testResults.length === 0) {
      toast.error('No test results to send. Run tests first.');
      return;
    }

    setIsSendingReport(true);
    
    try {
      const summary = testResultReporter.generateSummary(testResults);
      
      // Save to database
      await testResultReporter.saveToDatabase(summary);
      toast.success('Test results saved to database');
      
      // Send via Outlook
      const result = await testResultReporter.sendViaOutlook(summary);
      
      if (result.success) {
        toast.success(`✅ Test report sent via Outlook! Check your inbox: ${user.email}`);
        setLastReportSent(result.messageId || new Date().toISOString());
      } else {
        toast.error(`Failed to send report: ${result.error}`);
      }
    } catch (error) {
      console.error('Failed to send report:', error);
      toast.error('Failed to send test report');
    } finally {
      setIsSendingReport(false);
    }
  };

  // Download report as HTML
  const downloadReport = () => {
    if (testResults.length === 0) {
      toast.error('No test results to download. Run tests first.');
      return;
    }

    try {
      const summary = testResultReporter.generateSummary(testResults);
      const blob = new Blob([JSON.stringify(summary, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `test-results-${summary.executionId}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Test results downloaded');
    } catch (error) {
      console.error('Failed to download report:', error);
      toast.error('Failed to download report');
    }
  };

  // Get status icon
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'success':
        return <CheckCircle2 className="w-5 h-5 text-green-500" />;
      case 'failed':
        return <XCircle className="w-5 h-5 text-red-500" />;
      case 'running':
        return <RefreshCw className="w-5 h-5 text-blue-500 animate-spin" />;
      case 'skipped':
        return <AlertCircle className="w-5 h-5 text-yellow-500" />;
      default:
        return <Clock className="w-5 h-5 text-gray-400" />;
    }
  };

  // Group tests by category
  const categories = Array.from(new Set(tests.map(t => t.category)));

  if (!user) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Card className="w-96">
          <CardHeader>
            <CardTitle>Authentication Required</CardTitle>
            <CardDescription>Please sign in to run live tests</CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  if (!emailAccount) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Card className="w-96">
          <CardHeader>
            <CardTitle>Email Account Required</CardTitle>
            <CardDescription>Please connect Gmail or Outlook to run live tests</CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 max-w-7xl">
      <div className="mb-6">
        <h1 className="text-3xl font-bold mb-2">Comprehensive Live Test Suite</h1>
        <p className="text-muted-foreground">
          Testing all API calls, JSON schemas, database queries, agents, and workflows with stored authentication
        </p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold">{summary.total}</div>
            <div className="text-sm text-muted-foreground">Total Tests</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold text-green-500">{summary.passed}</div>
            <div className="text-sm text-muted-foreground">Passed</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold text-red-500">{summary.failed}</div>
            <div className="text-sm text-muted-foreground">Failed</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold text-yellow-500">{summary.skipped}</div>
            <div className="text-sm text-muted-foreground">Skipped</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold">{(summary.duration / 1000).toFixed(2)}s</div>
            <div className="text-sm text-muted-foreground">Duration</div>
          </CardContent>
        </Card>
      </div>

      {/* Control Buttons */}
      <div className="flex gap-3 mb-6 flex-wrap">
        <Button
          onClick={runAllTests}
          disabled={isRunning}
          className="gap-2"
        >
          {isRunning ? (
            <>
              <RefreshCw className="w-4 h-4 animate-spin" />
              Running Tests...
            </>
          ) : (
            <>
              <Play className="w-4 h-4" />
              Run All Tests
            </>
          )}
        </Button>
        
        <Button
          onClick={sendReportViaOutlook}
          disabled={isRunning || isSendingReport || testResults.length === 0}
          variant="default"
          className="gap-2"
        >
          {isSendingReport ? (
            <>
              <RefreshCw className="w-4 h-4 animate-spin" />
              Sending...
            </>
          ) : (
            <>
              <Mail className="w-4 h-4" />
              Send Report via Outlook
            </>
          )}
        </Button>

        <Button
          onClick={downloadReport}
          disabled={isRunning || testResults.length === 0}
          variant="outline"
          className="gap-2"
        >
          <Download className="w-4 h-4" />
          Download JSON
        </Button>

        <Button
          onClick={resetTests}
          variant="outline"
          disabled={isRunning}
        >
          Reset
        </Button>

        {lastReportSent && (
          <Badge variant="secondary" className="ml-auto flex items-center gap-2 px-4 py-2">
            <CheckCircle2 className="w-4 h-4 text-green-500" />
            Last report sent
          </Badge>
        )}
      </div>

      {/* Current Test Status */}
      {isRunning && currentTest && (
        <Alert className="mb-6">
          <RefreshCw className="w-4 h-4 animate-spin" />
          <AlertDescription>
            Currently running: <strong>{currentTest}</strong>
          </AlertDescription>
        </Alert>
      )}

      {/* Report Status */}
      {lastReportSent && !isRunning && (
        <Alert className="mb-6 border-green-200 bg-green-50">
          <CheckCircle2 className="w-4 h-4 text-green-600" />
          <AlertDescription className="text-green-900">
            ✅ Test report successfully sent to <strong>{user.email}</strong> via Outlook. Check your inbox for detailed results!
          </AlertDescription>
        </Alert>
      )}

      {/* Test Results */}
      <Tabs defaultValue="all" className="w-full">
        <TabsList>
          <TabsTrigger value="all">All Tests</TabsTrigger>
          {categories.map(category => (
            <TabsTrigger key={category} value={category}>
              {category}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="all" className="mt-6">
          <ScrollArea className="h-[600px]">
            <div className="space-y-4">
              {testResults.map((test) => (
                <Card key={test.id}>
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3">
                        {getStatusIcon(test.status)}
                        <div>
                          <CardTitle className="text-lg">{test.name}</CardTitle>
                          <CardDescription>{test.category}</CardDescription>
                        </div>
                      </div>
                      <Badge variant={test.status === 'success' ? 'default' : test.status === 'failed' ? 'destructive' : 'secondary'}>
                        {test.status}
                      </Badge>
                    </div>
                  </CardHeader>
                  {(test.result || test.error || test.apiCalls) && (
                    <CardContent>
                      {test.duration && (
                        <div className="text-sm text-muted-foreground mb-2">
                          Duration: {test.duration}ms
                        </div>
                      )}
                      {test.error && (
                        <div className="text-sm text-red-500 mb-2">
                          Error: {test.error}
                        </div>
                      )}
                      {test.result && (
                        <details className="text-sm">
                          <summary className="cursor-pointer font-medium mb-2">View Result</summary>
                          <pre className="bg-muted p-3 rounded-lg overflow-x-auto">
                            {JSON.stringify(test.result, null, 2)}
                          </pre>
                        </details>
                      )}
                      {test.apiCalls && test.apiCalls.length > 0 && (
                        <div className="mt-3">
                          <div className="text-sm font-medium mb-2">API Calls:</div>
                          {test.apiCalls.map((call, idx) => (
                            <div key={idx} className="text-xs bg-muted p-2 rounded mb-1">
                              <div className="flex justify-between">
                                <span>{call.method} {call.endpoint}</span>
                                <Badge variant="outline" className="text-xs">
                                  {call.status} - {call.duration}ms
                                </Badge>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  )}
                </Card>
              ))}
            </div>
          </ScrollArea>
        </TabsContent>

        {categories.map(category => (
          <TabsContent key={category} value={category} className="mt-6">
            <ScrollArea className="h-[600px]">
              <div className="space-y-4">
                {testResults.filter(t => t.category === category).map((test) => (
                  <Card key={test.id}>
                    <CardHeader className="pb-3">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-3">
                          {getStatusIcon(test.status)}
                          <div>
                            <CardTitle className="text-lg">{test.name}</CardTitle>
                          </div>
                        </div>
                        <Badge variant={test.status === 'success' ? 'default' : test.status === 'failed' ? 'destructive' : 'secondary'}>
                          {test.status}
                        </Badge>
                      </div>
                    </CardHeader>
                    {(test.result || test.error || test.apiCalls) && (
                      <CardContent>
                        {test.duration && (
                          <div className="text-sm text-muted-foreground mb-2">
                            Duration: {test.duration}ms
                          </div>
                        )}
                        {test.error && (
                          <div className="text-sm text-red-500 mb-2">
                            Error: {test.error}
                          </div>
                        )}
                        {test.result && (
                          <details className="text-sm">
                            <summary className="cursor-pointer font-medium mb-2">View Result</summary>
                            <pre className="bg-muted p-3 rounded-lg overflow-x-auto">
                              {JSON.stringify(test.result, null, 2)}
                            </pre>
                          </details>
                        )}
                        {test.apiCalls && test.apiCalls.length > 0 && (
                          <div className="mt-3">
                            <div className="text-sm font-medium mb-2">API Calls:</div>
                            {test.apiCalls.map((call, idx) => (
                              <div key={idx} className="text-xs bg-muted p-2 rounded mb-1">
                                <div className="flex justify-between">
                                  <span>{call.method} {call.endpoint}</span>
                                  <Badge variant="outline" className="text-xs">
                                    {call.status} - {call.duration}ms
                                  </Badge>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </CardContent>
                    )}
                  </Card>
                ))}
              </div>
            </ScrollArea>
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
